import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { reportEditStore } from '@stores/dictionaryStores/reports/reportEditStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { ReportForm } from './ReportForm';
import { validate } from './validateSchema';

const ReportEditComponent = () => {
  const { code } = useParams<{ code: string }>();
  const { push } = useHistory();
  const { setNew, fetch, setEditMode, isLoading, report, save, isNew, isEditMode } = reportEditStore;

  const { state } = useLocation();

  const permission = usePermission('CmReport', reportEditStore);

  useEffect(() => {
    if (code !== NEW_ENTITY_ID) {
      fetch(code);
    } else {
      setNew(true);
    }

    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, code, setNew, setEditMode]);

  const initialValues = useMemo(() => {
    if (state && state.code) {
      return { reportGroupCode: state.code };
    }

    return isLoading ? {} : toJS({ ...report, id: report?.code } || {}); // id нужен для проверки на уникальность кода
  }, [state, isLoading, report]);

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);
      if (validation) {
        return validation;
      }

      (async () => {
        const reportCode = await save(values);
        if (isNew && reportCode) {
          push(`${RoutePath.DICTIONARY_REPORT}/${reportCode}`);
        }
      })();

      return undefined;
    },
    [isNew, permission, push, save],
  );

  const goToReports = useCallback(() => push(RoutePath.DICTIONARY_REPORT), [push]);

  return (
    <DesignLayout title="Справочник: Отчеты" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <FormWithActions store={reportEditStore} goBackLink={goToReports}>
          <ReportForm />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
};

export const ReportEdit = observer(ReportEditComponent);
